import React, { useState, useMemo, useEffect } from 'react';
import './AllOrdersTable.css';

// Split one CSV line, honoring simple double-quoted fields.
function splitCsvLine(line) {
  const out = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') inQ = !inQ;
    else if (c === ',' && !inQ) { out.push(cur); cur = ''; }
    else cur += c;
  }
  out.push(cur);
  return out.map(s => s.trim());
}

// Parse a legacy upload sheet: header rows (School Name / District / Invoice #) then a
// "Bag Serial # | Student ID | First | Last | Brand | Model | RFID" table.
function parseLegacySheet(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
  let orderNumber = '';
  let company = '';
  let headerIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    const cells = splitCsvLine(lines[i]);
    const key = (cells[0] || '').toLowerCase().replace(/\s|#/g, '');
    if (key.startsWith('invoice')) orderNumber = (cells[1] || '').trim();
    else if (key === 'district') { const v = (cells[1] || '').trim(); if (v && !company) company = v; }
    else if (key === 'schoolname') { const v = (cells[1] || '').trim(); if (v) company = v; }
    else if (key === 'bagserial' || (cells[0] || '').toLowerCase().startsWith('bag serial')) { headerIdx = i; break; }
  }
  const rows = [];
  if (headerIdx >= 0) {
    const header = splitCsvLine(lines[headerIdx]).map(h => h.toLowerCase());
    const serialCol = Math.max(0, header.findIndex(h => h.startsWith('bag serial')));
    let modelCol = header.findIndex(h => h === 'model');
    if (modelCol < 0) modelCol = 5;
    // The RFID column decides a GFSB-* bag's revision on the server (card = -V2, embedded = -V3,
    // N/A = -V1). It was previously read past, so every legacy import landed on whatever revision
    // the Model text happened to say. -1 = the sheet has no RFID column; the server then leaves the
    // revision alone rather than assuming "no RFID".
    const rfidCol = header.findIndex(h => h.startsWith('rfid'));
    for (let i = headerIdx + 1; i < lines.length; i++) {
      const cells = splitCsvLine(lines[i]);
      // Sheets prefix the serial with the column label ("SN:", "SN#", "SERIAL#") and the model with
      // "SKU:". Strip those so the value matches what's in the system — a serial stored as
      // "SERIAL# 1126802" can never be found by the number on the tag, so the bag can't be scanned
      // or assigned. Rows without a serial (e.g. a trailing student roster pasted below the bag
      // list) are skipped. The server re-applies the same cleanup; this keeps the preview honest.
      // The label must be followed by a separator, or "SN" would eat the start of a real serial
      // like "SNAP-77". Mirrors sanitizeSerialInput (fBagRepo.kt).
      const serialNumber = (cells[serialCol] || '')
        .replace(/^\s*(SERIAL|SN|S\/N)\s*(?:[#:]\s*|\s+)/i, '')
        .trim();
      if (!serialNumber) continue;
      const model = (cells[modelCol] || '').trim().replace(/^SKU\s*[:#]\s*/i, '').trim();
      const rfid = rfidCol >= 0 ? (cells[rfidCol] || '').trim() : '';
      rows.push({ serialNumber, model, rfid });
    }
  }
  return { orderNumber, company, rows };
}

// `initialQuery` is set when you arrive from the global search — it filters the list down to the
// order you clicked, which matters here because claimed orders sit in a collapsed group.
function AllOrdersTable({ orders, loading, initialQuery = '', onOpenSchool, onRefreshOrders }) {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(initialQuery);
  // Optimistic local overrides so the toggle flips instantly (no wait on the PATCH + refetch).
  const [internalOverrides, setInternalOverrides] = useState({});
  const isInternal = (o) => (o && o.id in internalOverrides ? internalOverrides[o.id] : !!(o && o.internal));

  // Flag/unflag an order as an internal test order. Instant UI; server call in the background.
  const toggleInternal = async (order) => {
    const next = !isInternal(order);
    setInternalOverrides(prev => ({ ...prev, [order.id]: next }));
    // The row is about to jump groups. Expand its destination so it stays visible (and
    // re-toggleable) instead of vanishing into a collapsed group, and flash it so the
    // move is obvious.
    const dest = next
      ? 'internal'
      : ((order.status || '').toLowerCase() === 'claimed' ? 'claimed' : order.serialized ? 'ready' : 'upcoming');
    setCollapsed(prev => ({ ...prev, [dest]: false }));
    setJustMovedId(order.id);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const response = await fetch(`${apiEndpoint}/admin/orders/${order.id}/internal`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ internal: next })
      });
      if (!response.ok) throw new Error(`status ${response.status}`);
    } catch (err) {
      console.error('Error toggling internal:', err);
      setInternalOverrides(prev => ({ ...prev, [order.id]: !next })); // revert
      alert('Failed to update the internal flag. Please try again.');
    }
  };

  // Per-order email drafts (so typing doesn't fight refreshes), plus in-flight state.
  const [emailDrafts, setEmailDrafts] = useState({});
  const [savingEmailId, setSavingEmailId] = useState(null);
  // Extra setup-email recipients, comma-separated. Copied on the setup email, but never the
  // claim key — that stays the customer email.
  const [altEmailDrafts, setAltEmailDrafts] = useState({});
  const [savingAltEmailId, setSavingAltEmailId] = useState(null);
  const [finishingId, setFinishingId] = useState(null);
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [sendMenuId, setSendMenuId] = useState(null); // which order's "choose email version" menu is open
  const [legacyPreview, setLegacyPreview] = useState(null); // {orderNumber, company, rows} | null
  // Persistent post-upload result (so the skipped-serial report doesn't vanish on an outside click).
  const [legacyResult, setLegacyResult] = useState(null); // {orderNumber, recorded, conflicts} | null
  // Editable order # for the preview — seeded from the sheet's Invoice # row, but some sheets
  // (e.g. per-school rosters) have none, so the user can type it in before creating.
  const [legacyOrderNo, setLegacyOrderNo] = useState('');
  const [creatingLegacy, setCreatingLegacy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  // Whether ShipStation is actually connected (creds on the server). Gray out the sync button
  // until it is; flips on automatically once the API keys are configured.
  const [ssConfigured, setSsConfigured] = useState(false);
  // "Claim this order for a school or district" (staff claiming on a customer's behalf).
  const [claimSchools, setClaimSchools] = useState([]);
  const [claimDistricts, setClaimDistricts] = useState([]);
  const [claimSel, setClaimSel] = useState({});   // order.id -> "school:<id>" | "district:<id>"
  const [claimingId, setClaimingId] = useState(null);
  // Collapsible order groups. The two archival groups (claimed, internal) start collapsed;
  // the two actionable groups (upcoming, ready) start open.
  const [collapsed, setCollapsed] = useState({ claimed: true, internal: true, archived: true });
  const toggleGroup = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  // The order most recently flagged internal/customer — briefly highlighted after it moves groups.
  const [justMovedId, setJustMovedId] = useState(null);
  useEffect(() => {
    if (justMovedId == null) return;
    const t = setTimeout(() => setJustMovedId(null), 1500);
    return () => clearTimeout(t);
  }, [justMovedId]);

  // Is ShipStation connected? Controls whether the sync button is active.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
        const res = await fetch(`${apiEndpoint}/admin/shipstation-status`, { headers: authHeaders(), cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setSsConfigured(!!data.configured);
      } catch (err) {
        console.error('Error checking ShipStation status:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load schools + districts once, for the "claim for…" picker.
  useEffect(() => {
    const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
    (async () => {
      try {
        const [s, d] = await Promise.all([
          fetch(`${apiEndpoint}/admin/schools`, { headers: authHeaders(), cache: 'no-store' }).then(r => (r.ok ? r.json() : [])),
          fetch(`${apiEndpoint}/admin/districts`, { headers: authHeaders(), cache: 'no-store' }).then(r => (r.ok ? r.json() : [])),
        ]);
        setClaimSchools(Array.isArray(s) ? s : (s.schools || []));
        setClaimDistricts(Array.isArray(d) ? d : (d.districts || []));
      } catch (err) {
        console.error('Error loading schools/districts for claim:', err);
      }
    })();
  }, []);

  // Claim an unclaimed order for the picked school or district (POST /admin/orders/{id}/claim).
  const handleClaimForOwner = async (o) => {
    const sel = claimSel[o.id];
    if (!sel) { alert('Pick a school or district first.'); return; }
    const [type, idStr] = sel.split(':');
    const body = type === 'school' ? { schoolId: parseInt(idStr, 10) } : { districtId: parseInt(idStr, 10) };
    setClaimingId(o.id);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const response = await fetch(`${apiEndpoint}/admin/orders/${o.id}/claim`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `status ${response.status}`);
      alert(`Order ${o.number} claimed for ${data.claimedBy || 'the selected owner'} (${data.unitsAdded ?? o.orderCount} units).`);
      setClaimSel(prev => { const n = { ...prev }; delete n[o.id]; return n; });
      if (onRefreshOrders) onRefreshOrders();
    } catch (err) {
      console.error('Error claiming order:', err);
      alert(`Could not claim order: ${err.message}`);
    } finally {
      setClaimingId(null);
    }
  };

  // Inline expand: clicking an order number reveals its product breakdown + serials.
  const [expandedId, setExpandedId] = useState(null);
  const [serials, setSerials] = useState({}); // orderId -> OrderSerialDTO[]
  const [loadingSerialsId, setLoadingSerialsId] = useState(null);

  // Order-notes drafts in the expand panel (same field as the per-school Orders tab).
  const [noteDrafts, setNoteDrafts] = useState({});
  const [savingNoteId, setSavingNoteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    setNoteDrafts(prev => {
      const next = { ...prev };
      orders.forEach(o => { if (!(o.id in next)) next[o.id] = o.notes || ''; });
      return next;
    });
  }, [orders]);

  const handleSaveNotes = async (order) => {
    if (savingNoteId) return;
    setSavingNoteId(order.id);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const response = await fetch(`${apiEndpoint}/admin/orders/${order.id}/notes`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ notes: noteDrafts[order.id] ?? '' })
      });
      if (response.ok) {
        if (onRefreshOrders) await onRefreshOrders();
      } else {
        alert('Failed to save notes. Please try again.');
      }
    } catch (err) {
      console.error('Error saving order notes:', err);
      alert('Error saving notes. Please try again.');
    } finally {
      setSavingNoteId(null);
    }
  };

  const handleArchiveOrder = async (order) => {
    const next = !order.archived;
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const response = await fetch(`${apiEndpoint}/admin/orders/${order.id}/archived`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ archived: next }),
      });
      if (response.ok) {
        if (expandedId === order.id) setExpandedId(null);
        if (onRefreshOrders) await onRefreshOrders();
      } else {
        alert('Failed to update the order. Please try again.');
      }
    } catch (err) {
      console.error('Error archiving order:', err);
      alert('Error updating the order. Please try again.');
    }
  };

  // Move a serial off THIS order onto the correct one (fix a mis-tied/mis-claimed serial).
  const handleTransferSerial = async (order) => {
    const serial = window.prompt(`Transfer a serial FROM order #${order.number} to another order.\n\nEnter the serial number to move:`);
    if (!serial || !serial.trim()) return;
    const targetOrder = window.prompt(`Move serial ${serial.trim()} to which order number?`);
    if (!targetOrder || !targetOrder.trim()) return;
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const response = await fetch(`${apiEndpoint}/admin/bags/${encodeURIComponent(serial.trim())}/transfer-order`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderNumber: targetOrder.trim() }),
      });
      const body = await response.json().catch(() => ({}));
      if (response.ok) {
        alert(`Serial ${serial.trim()} moved to order #${body.orderNumber}.`);
        setSerials(prev => { const n = { ...prev }; delete n[order.id]; return n; }); // force re-fetch of this order's serials
        if (onRefreshOrders) await onRefreshOrders();
      } else {
        alert(body.error || 'Could not transfer the serial. Check the serial and target order #.');
      }
    } catch (err) {
      console.error('Error transferring serial:', err);
      alert('Error transferring the serial. Please try again.');
    }
  };

  const handleDeleteOrder = async (order) => {
    if (deletingId) return;
    const claimed = (order.status || '').toLowerCase() === 'claimed';
    const bagCount = (serials[order.id] || []).length || order.orderCount || 0;
    const warn = claimed
      ? `Order #${order.number} is CLAIMED by ${ownerLabel(order) || 'a customer'}. Deleting it removes the order record`
      : `Delete order #${order.number}`;
    const msg = `${warn}${bagCount ? ` and unlinks its ${bagCount} bag(s) (the bags are NOT deleted; they stay in the system, just detached from this order)` : ''}.\n\nThis cannot be undone. Continue?`;
    if (!window.confirm(msg)) return;
    setDeletingId(order.id);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const response = await fetch(`${apiEndpoint}/admin/orders/${order.id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (response.ok) {
        if (expandedId === order.id) setExpandedId(null);
        if (onRefreshOrders) await onRefreshOrders();
      } else {
        const body = await response.json().catch(() => ({}));
        alert(body.error || 'Failed to delete the order. Please try again.');
      }
    } catch (err) {
      console.error('Error deleting order:', err);
      alert('Error deleting the order. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const toggleExpand = async (order) => {
    if (expandedId === order.id) { setExpandedId(null); return; }
    setExpandedId(order.id);
    if (serials[order.id]) return; // already loaded
    setLoadingSerialsId(order.id);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const response = await fetch(`${apiEndpoint}/admin/orders/${order.id}/serials`, {
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSerials(prev => ({ ...prev, [order.id]: data }));
      } else {
        alert('Failed to load products for this order.');
        setExpandedId(null);
      }
    } catch (err) {
      console.error('Error loading order products:', err);
      alert('Error loading products.');
      setExpandedId(null);
    } finally {
      setLoadingSerialsId(null);
    }
  };

  // Group an order's serials into a per-SKU product summary (name + exact SKU + count).
  const productSummary = (list) => {
    const bySku = {};
    (list || []).forEach(s => {
      if (!bySku[s.sku]) bySku[s.sku] = { sku: s.sku, displayName: s.displayName, count: 0 };
      bySku[s.sku].count += 1;
    });
    return Object.values(bySku).sort((a, b) => b.count - a.count);
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Arriving from the global search: filter to the order that was clicked, and open every group
  // so it's visible — claimed/internal/archived orders start collapsed, and a hit that lands in
  // one of those would otherwise look like it went nowhere.
  useEffect(() => {
    if (!initialQuery) return;
    setSearchQuery(initialQuery);
    setDebouncedQuery(initialQuery);
    setCollapsed({ claimed: false, internal: false, archived: false });
  }, [initialQuery]);

  // Seed email drafts from the staff-entered email, falling back to the legacy ShipStation
  // email for older orders that never had one set. Don't clobber an active edit.
  useEffect(() => {
    setEmailDrafts(prev => {
      const next = { ...prev };
      orders.forEach(o => {
        if (!(o.id in next)) next[o.id] = o.notifyEmail || o.ssEmail || '';
      });
      return next;
    });
    setAltEmailDrafts(prev => {
      const next = { ...prev };
      orders.forEach(o => {
        if (!(o.id in next)) next[o.id] = o.alternateEmails || '';
      });
      return next;
    });
  }, [orders]);

  const formatDate = (ts) => {
    if (!ts) return '-';
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const ownerLabel = (o) => o.schoolName || o.districtName || '';

  const authHeaders = () => {
    const username = localStorage.getItem('admin_username');
    const password = localStorage.getItem('admin_password');
    return {
      'Content-Type': 'application/json',
      'Authorization': 'Basic ' + btoa(`${username}:${password}`)
    };
  };

  const handleSaveEmail = async (order) => {
    if (savingEmailId) return;
    setSavingEmailId(order.id);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const response = await fetch(`${apiEndpoint}/admin/orders/${order.id}/email`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ email: emailDrafts[order.id] ?? '' })
      });
      if (response.ok) {
        if (onRefreshOrders) await onRefreshOrders();
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.error || 'Failed to save email. Please try again.');
      }
    } catch (err) {
      console.error('Error saving order email:', err);
      alert('Error saving email. Please try again.');
    } finally {
      setSavingEmailId(null);
    }
  };

  const handleSaveAltEmails = async (order) => {
    if (savingAltEmailId) return;
    setSavingAltEmailId(order.id);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const response = await fetch(`${apiEndpoint}/admin/orders/${order.id}/alternate-emails`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ emails: altEmailDrafts[order.id] ?? '' })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        // Show the stored list back, or the field stays "unsaved" forever over whitespace and
        // duplicates the server strips out.
        if (typeof data.alternateEmails === 'string') {
          setAltEmailDrafts(prev => ({ ...prev, [order.id]: data.alternateEmails }));
        }
        if (onRefreshOrders) await onRefreshOrders();
      } else {
        alert(data.error || 'Failed to save the additional emails. Please try again.');
      }
    } catch (err) {
      console.error('Error saving additional order emails:', err);
      alert('Error saving the additional emails. Please try again.');
    } finally {
      setSavingAltEmailId(null);
    }
  };

  const handleFinishSerializing = async (order) => {
    if (finishingId) return;
    // No email required to finish — warehouse just marks it ready (they note the customer). If an
    // email edit is on screen, save it so it's on file for when Jamie sends the setup email.
    const email = (emailDrafts[order.id] ?? '').trim();
    if (email && email !== (order.notifyEmail || '')) {
      await handleSaveEmail(order);
    }
    const ok = window.confirm(
      `Mark order ${order.number} as finished serializing?\n\n` +
      `This only flags the order as ready. It does NOT email the customer. ` +
      `Use "Send setup email" afterward to email them.`
    );
    if (!ok) return;
    setFinishingId(order.id);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const response = await fetch(`${apiEndpoint}/admin/orders/${order.id}/finish-serializing`, {
        method: 'POST',
        headers: authHeaders()
      });
      if (response.ok) {
        alert(`Order ${order.number} marked finished. Click "Send setup email" to email the customer.`);
        if (onRefreshOrders) await onRefreshOrders();
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.error || 'Failed to finish serialization. Please try again.');
      }
    } catch (err) {
      console.error('Error finishing serialization:', err);
      alert('Error finishing serialization. Please try again.');
    } finally {
      setFinishingId(null);
    }
  };

  // Send (or resend) the "order is ready to set up" email. Separate from finishing serialization,
  // and callable repeatedly so staff can resend if the customer never received it.
  const handleSendEmail = async (order, version) => {
    if (sendingEmailId) return;
    const email = (emailDrafts[order.id] ?? order.notifyEmail ?? '').trim();
    if (!email) { alert('Add a customer email first.'); return; }
    // Persist any unsaved edit so the email goes to what's on screen.
    if (email !== (order.notifyEmail || '')) {
      await handleSaveEmail(order);
    }
    const label = version === 'recurring' ? 'RECURRING-client' : 'NEW-client';
    const resend = !!order.orderEmailSentAt;
    // Everyone on the additional-emails list gets a copy too, so name them here rather than
    // letting the confirm imply the customer is the only recipient.
    const alternates = (order.alternateEmails || '').split(',').map(e => e.trim()).filter(Boolean);
    const recipients = [email, ...alternates].join(', ');
    if (!window.confirm(`${resend ? 'Resend' : 'Send'} the ${label} setup email to ${recipients}?`)) return;
    setSendingEmailId(order.id);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const response = await fetch(`${apiEndpoint}/admin/orders/${order.id}/send-order-email`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: version === 'recurring' ? 'recurring' : 'new' }),
      });
      if (response.ok) {
        alert(`Setup email sent to ${recipients}.`);
        if (onRefreshOrders) await onRefreshOrders();
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.error || 'Failed to send the email. Please try again.');
      }
    } catch (err) {
      console.error('Error sending setup email:', err);
      alert('Error sending the email. Please try again.');
    } finally {
      setSendingEmailId(null);
    }
  };

  // Pull customer email + ship-by date from ShipStation for every un-claimed order
  // (matched on order number). Email only fills blanks; ship-by always refreshes.
  const handleSyncShipStation = async () => {
    if (syncing) return;
    if (!window.confirm('Look up every unclaimed order in ShipStation and fill in the customer email (where blank) and ship-by date?')) return;
    setSyncing(true);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const response = await fetch(`${apiEndpoint}/admin/orders/sync-shipstation`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        alert(
          `ShipStation sync complete.\n\n` +
          `Orders checked: ${data.scanned ?? 0}\n` +
          `Matched in ShipStation: ${data.matched ?? 0}\n` +
          `Emails filled: ${data.filledEmail ?? 0}\n` +
          `Ship-by dates updated: ${data.updatedShipBy ?? 0}\n` +
          `No ShipStation match: ${data.notFound ?? 0}`
        );
        if (onRefreshOrders) await onRefreshOrders();
      } else {
        const partial = data.scanned != null
          ? `\n\nSynced ${data.filledEmail ?? 0} email(s) and ${data.updatedShipBy ?? 0} date(s) before stopping.`
          : '';
        alert((data.error || 'ShipStation sync failed. Please try again.') + partial);
        if (data.scanned != null && onRefreshOrders) await onRefreshOrders();
      }
    } catch (err) {
      console.error('Error syncing ShipStation:', err);
      alert('Error contacting the server for the ShipStation sync. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  // Legacy spreadsheet upload → parse client-side, preview, then create the order.
  const handleLegacyFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parseLegacySheet(text);
      if (parsed.rows.length === 0) {
        alert("Couldn't find any bag serial numbers in that spreadsheet.");
        return;
      }
      // A missing Invoice # is no longer fatal — the user can type one in the preview.
      setLegacyOrderNo(parsed.orderNumber || '');
      setLegacyPreview(parsed);
    } catch (err) {
      console.error('Error reading spreadsheet:', err);
      alert('Could not read that file. Please upload a .csv export.');
    }
  };

  // Break the preview down by model AND RFID type, since the RFID value decides the revision the
  // server will store (card = -V2, embedded = -V3, N/A = -V1). Showing them together makes a sheet
  // with a missing or unexpected RFID column obvious before the order is created.
  const legacyBreakdown = useMemo(() => {
    if (!legacyPreview) return [];
    const m = {};
    legacyPreview.rows.forEach(r => {
      const rfid = (r.rfid || '').trim();
      const k = (r.model || '(no model)') + (rfid ? ` · RFID: ${rfid}` : ' · RFID: (not in sheet)');
      m[k] = (m[k] || 0) + 1;
    });
    return Object.entries(m).map(([model, count]) => ({ model, count })).sort((a, b) => b.count - a.count);
  }, [legacyPreview]);

  const handleCreateLegacy = async () => {
    if (!legacyPreview || creatingLegacy) return;
    const orderNumber = (legacyOrderNo || '').trim();
    if (!orderNumber) {
      alert('Please enter an order / invoice number before creating.');
      return;
    }
    setCreatingLegacy(true);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const response = await fetch(`${apiEndpoint}/admin/orders/from-spreadsheet`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderNumber,
          company: legacyPreview.company || '',
          scope: null,
          rows: legacyPreview.rows,
        }),
      });
      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        // Each conflict = { serialNumber, orderNumber } — a serial that couldn't be added
        // because it's already attached to another order. Tell staff exactly which + where.
        const conflicts = data.conflicts || [];
        if (conflicts.length) console.warn('Legacy upload conflicts (serial -> order):', conflicts);
        // Show a persistent result panel (not a one-shot alert) so the skipped-serial list stays
        // on screen — and stays copyable — until the user explicitly closes it.
        setLegacyResult({
          orderNumber: data.orderNumber || orderNumber,
          recorded: data.recorded ?? legacyPreview.rows.length,
          conflicts,
          badFormat: data.badFormat || [],
        });
        setLegacyPreview(null);
        if (onRefreshOrders) await onRefreshOrders();
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.error || 'Failed to create the order. Please try again.');
      }
    } catch (err) {
      console.error('Error creating legacy order:', err);
      alert('Error creating the order. Please try again.');
    } finally {
      setCreatingLegacy(false);
    }
  };

  const filtered = useMemo(() => {
    if (!debouncedQuery) return orders;
    const q = debouncedQuery.toLowerCase();
    return orders.filter(o =>
      (o.number || '').toLowerCase().includes(q) ||
      ownerLabel(o).toLowerCase().includes(q) ||
      (o.notifyEmail || '').toLowerCase().includes(q)
    );
  }, [orders, debouncedQuery]);

  // Sort each matching order into exactly one group. Internal takes precedence over status
  // so test orders never clutter the real ones; otherwise claimed → ready → in-progress.
  const groups = useMemo(() => {
    const g = { upcoming: [], ready: [], claimed: [], internal: [], archived: [] };
    filtered.forEach(o => {
      if (o.archived) g.archived.push(o);            // archived takes precedence — out of the way
      else if (isInternal(o)) g.internal.push(o);
      else if ((o.status || '').toLowerCase() === 'claimed') g.claimed.push(o);
      else if (o.serialized) g.ready.push(o);
      else g.upcoming.push(o);
    });
    return g;
  }, [filtered, internalOverrides]);

  // Flatten the groups into one render list of {header|empty|row} items so the whole thing
  // stays a single <table> (aligned columns) with collapsible section headers interleaved.
  const renderRows = useMemo(() => {
    const defs = [
      { key: 'upcoming', title: 'Upcoming / in progress' },
      { key: 'ready', title: 'Finished serializing. Ready to claim' },
      { key: 'claimed', title: 'Claimed' },
      { key: 'internal', title: 'Internal use / test orders' },
      { key: 'archived', title: 'Archived' },
    ];
    const list = [];
    defs.forEach(g => {
      list.push({ type: 'header', group: g, count: groups[g.key].length });
      if (!collapsed[g.key]) {
        if (groups[g.key].length === 0) list.push({ type: 'empty', group: g });
        else groups[g.key].forEach(o => list.push({ type: 'row', order: o }));
      }
    });
    return list;
  }, [groups, collapsed]);

  const claimedCount = useMemo(() => orders.filter(o => (o.status || '').toLowerCase() === 'claimed').length, [orders]);
  const unclaimedCount = orders.length - claimedCount;

  const stop = (e) => e.stopPropagation();

  return (
    <div className="all-orders">
      <details className="orders-howto">
        <summary>How to finish an order &amp; send the customer their setup email</summary>
        <ol>
          <li><strong>Confirm the total.</strong> Click an order's number to expand it and check the product/unit breakdown matches what was serialized.</li>
          <li><strong>Add the customer to the Notes field and Save.</strong> Warehouse enters who the order is for (name and any details from ShipStation) in the order's Notes field and clicks Save, so the sales rep knows whose order this is.</li>
          <li><strong>Mark finished.</strong> Click "Mark finished" to flag the order serialized. This does <strong>not</strong> email the customer.</li>
          <li><strong>Sales rep sends the email.</strong> The sales rep enters the customer's email, then clicks "Send setup email" and picks the version: New client (order setup with account setup and onboarding) or Recurring client (add the order to their existing account). Use "Resend setup email" if they never got it.</li>
        </ol>
      </details>
      <div className="all-orders-toolbar">
        <div className="all-orders-counts">
          <span className="count-pill claimed">{claimedCount} claimed</span>
          <span className="count-pill unclaimed">{unclaimedCount} unclaimed</span>
        </div>
        <div className="all-orders-controls">
          <input
            className="all-orders-search"
            type="text"
            placeholder="Search order #, school, or email…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button
            className="ss-sync-btn"
            onClick={handleSyncShipStation}
            disabled={syncing || !ssConfigured}
            title={ssConfigured
              ? 'Pull customer email + ship-by date from ShipStation for unclaimed orders (matched on order number)'
              : 'ShipStation isn’t connected yet. Coming soon'}
          >
            {syncing ? 'Syncing…' : (ssConfigured ? '⟳ Sync from ShipStation' : '⟳ Sync from ShipStation (not connected)')}
          </button>
          <label className="legacy-upload-btn" title="Create a claimable order from a legacy serial spreadsheet (no AC scan)">
            + Legacy order from spreadsheet
            <input type="file" accept=".csv,text/csv" onChange={handleLegacyFile} style={{ display: 'none' }} />
          </label>
        </div>
      </div>

      {loading ? (
        <div className="all-orders-empty">Loading orders…</div>
      ) : filtered.length === 0 ? (
        <div className="all-orders-empty">No orders match.</div>
      ) : (
        <div className="all-orders-table-wrap">
          <table className="all-orders-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Status</th>
                <th>Claimed by</th>
                <th>Customer email (claim key)</th>
                <th>Additional emails</th>
                <th>Serialized</th>
                <th>Date</th>
                <th>Ship by</th>
                <th className="num">Units</th>
              </tr>
            </thead>
            <tbody>
              {renderRows.map((item) => {
                if (item.type === 'header') {
                  const g = item.group;
                  const isCollapsed = !!collapsed[g.key];
                  return (
                    <tr
                      key={`h-${g.key}`}
                      className={`orders-group-header-row ${g.key}${isCollapsed ? ' collapsed' : ''}`}
                      onClick={() => toggleGroup(g.key)}
                    >
                      <td colSpan={9}>
                        <span className="orders-group-caret">{isCollapsed ? '▸' : '▾'}</span>
                        <span className="orders-group-title">{g.title}</span>
                        <span className="orders-group-count">{item.count}</span>
                      </td>
                    </tr>
                  );
                }
                if (item.type === 'empty') {
                  return (
                    <tr key={`e-${item.group.key}`} className="orders-group-empty-row">
                      <td colSpan={9}>No orders in this group.</td>
                    </tr>
                  );
                }
                const o = item.order;
                const claimed = (o.status || '').toLowerCase() === 'claimed';
                const owner = ownerLabel(o);
                const clickable = claimed && o.schoolId && onOpenSchool;
                const draft = emailDrafts[o.id] ?? '';
                const emailDirty = draft !== (o.notifyEmail || '');
                const altDraft = altEmailDrafts[o.id] ?? '';
                const altEmailDirty = altDraft !== (o.alternateEmails || '');
                // Email is editable at ANY time — including after serialization and after the order
                // is claimed — so a typo can be fixed and the setup email resent whenever needed.
                const emailLocked = false;
                // Warehouse can mark finished without a customer email (they note the customer;
                // Jamie sends the setup email afterward).
                const canFinish = !o.serialized && !claimed;
                const expanded = expandedId === o.id;
                return (
                  <React.Fragment key={o.id}>
                  <tr
                    className={`${clickable ? 'clickable' : ''}${justMovedId === o.id ? ' just-moved' : ''}`}
                    onClick={clickable ? () => onOpenSchool(o.schoolId) : undefined}
                    title={clickable ? 'Open this school' : ''}
                  >
                    <td className="order-num" onClick={stop}>
                      <button
                        className="order-num-btn"
                        onClick={() => toggleExpand(o)}
                        title="Show this order's products"
                      >
                        <span className="order-num-caret">{expanded ? '▾' : '▸'}</span>
                        {o.number}
                      </button>
                      {o.notes && o.notes.trim() ? (
                        // Quick peek at the notes on hover (instant CSS tooltip, no native delay);
                        // full editable notes live in the expand.
                        <span className="order-notes-hint">
                          📝
                          <span className="order-notes-tip">{o.notes}</span>
                        </span>
                      ) : null}
                    </td>
                    <td onClick={stop}>
                      <span className={`status-badge ${claimed ? 'claimed' : 'unclaimed'}`}>
                        <span className="dot" />
                        {claimed ? 'Claimed' : 'Unclaimed'}
                      </span>
                      <button
                        className={`test-toggle ${isInternal(o) ? 'on' : ''}`}
                        onClick={() => toggleInternal(o)}
                        title={isInternal(o) ? 'Internal order. Click to mark as customer' : 'Customer order. Click to mark as internal'}
                      >
                        {isInternal(o) ? 'Internal' : 'Customer'}
                      </button>
                    </td>
                    <td>
                      {owner
                        ? <span>{owner}{o.districtName && o.schoolName ? '' : (o.districtName ? ' (district)' : '')}</span>
                        : <span className="muted">-</span>}
                    </td>
                    <td className="order-email-cell" onClick={stop}>
                      <div className="order-email-wrap">
                        <input
                          className="order-email-input"
                          type="email"
                          value={draft}
                          disabled={emailLocked}
                          placeholder="customer@email.com"
                          onChange={(e) => setEmailDrafts(prev => ({ ...prev, [o.id]: e.target.value }))}
                          title={claimed ? 'Locked. Order already claimed' : ''}
                        />
                        {!emailLocked && emailDirty && (
                          <button
                            className="order-email-save"
                            onClick={() => handleSaveEmail(o)}
                            disabled={savingEmailId === o.id}
                          >
                            {savingEmailId === o.id ? '…' : 'Save'}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="order-alt-email-cell" onClick={stop}>
                      <div className="order-email-wrap">
                        <input
                          className="order-email-input"
                          type="text"
                          value={altDraft}
                          placeholder="cc@school.org, cc2@school.org"
                          onChange={(e) => setAltEmailDrafts(prev => ({ ...prev, [o.id]: e.target.value }))}
                          title="Also copied on the setup email. They cannot claim the order — the claim key stays the customer email."
                        />
                        {altEmailDirty && (
                          <button
                            className="order-email-save"
                            onClick={() => handleSaveAltEmails(o)}
                            disabled={savingAltEmailId === o.id}
                          >
                            {savingAltEmailId === o.id ? '…' : 'Save'}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="order-serialized-cell" onClick={stop}>
                      {claimed ? (
                        <span className="muted" title="Order already claimed">-</span>
                      ) : o.serialized ? (
                        <div className="order-serialized-actions" style={{ position: 'relative' }}>
                          <span className="serialized-badge done" title="Finished serializing">✓ Finished</span>
                          <button
                            className="send-email-btn"
                            onClick={() => setSendMenuId(sendMenuId === o.id ? null : o.id)}
                            disabled={sendingEmailId === o.id || !(emailDrafts[o.id] ?? o.notifyEmail ?? '').trim()}
                            title="Email the customer their setup / claim instructions"
                          >
                            {sendingEmailId === o.id ? 'Sending…' : (o.orderEmailSentAt ? 'Resend setup email' : 'Send setup email')}
                          </button>
                          {sendMenuId === o.id ? (
                            <div className="send-email-menu">
                              <div className="send-email-menu-title">Which version?</div>
                              <button className="send-email-menu-item" onClick={() => { setSendMenuId(null); handleSendEmail(o, 'new'); }}>
                                <strong>New client</strong><span className="send-email-menu-sub">account setup + onboarding</span>
                              </button>
                              <button className="send-email-menu-item" onClick={() => { setSendMenuId(null); handleSendEmail(o, 'recurring'); }}>
                                <strong>Recurring client</strong><span className="send-email-menu-sub">add to existing account</span>
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <button
                          className="finish-serializing-btn"
                          onClick={() => handleFinishSerializing(o)}
                          disabled={!canFinish || finishingId === o.id}
                          title={canFinish ? 'Flag this order finished (does not email the customer)' : 'Add a customer email first'}
                        >
                          {finishingId === o.id ? 'Marking…' : 'Mark finished'}
                        </button>
                      )}
                    </td>
                    <td>{formatDate(o.date)}</td>
                    <td className="order-shipby-cell">
                      {o.shipByDate ? o.shipByDate : <span className="muted">-</span>}
                    </td>
                    <td className="num">{o.orderCount}</td>
                  </tr>
                  {expanded && (
                    <tr className="order-expand-row">
                      <td colSpan={9}>
                        {loadingSerialsId === o.id ? (
                          <div className="order-expand-loading">Loading products…</div>
                        ) : (
                          <>
                          <div className="order-expand-notes">
                            <label className="order-expand-title">Notes (internal)</label>
                            <textarea
                              className="order-expand-notes-input"
                              value={noteDrafts[o.id] ?? ''}
                              onChange={(e) => setNoteDrafts(prev => ({ ...prev, [o.id]: e.target.value }))}
                              placeholder="Product iterations, batch info…"
                              rows={2}
                            />
                            <button
                              className="order-expand-notes-save"
                              onClick={() => handleSaveNotes(o)}
                              disabled={savingNoteId === o.id || (noteDrafts[o.id] ?? '') === (o.notes || '')}
                            >
                              {savingNoteId === o.id ? 'Saving…' : (noteDrafts[o.id] ?? '') === (o.notes || '') ? 'Saved' : 'Save notes'}
                            </button>
                          </div>
                          {!claimed && (
                            <div className="order-expand-claim">
                              <label className="order-expand-title">Claim this order for a school or district</label>
                              <div className="order-claim-row">
                                <select
                                  className="order-claim-select"
                                  value={claimSel[o.id] ?? ''}
                                  onChange={(e) => setClaimSel(prev => ({ ...prev, [o.id]: e.target.value }))}
                                >
                                  <option value="">Select a school or district…</option>
                                  <optgroup label="Schools">
                                    {claimSchools.map(s => <option key={`s${s.id}`} value={`school:${s.id}`}>{s.name}</option>)}
                                  </optgroup>
                                  <optgroup label="Districts">
                                    {claimDistricts.map(d => <option key={`d${d.id}`} value={`district:${d.id}`}>{d.name}</option>)}
                                  </optgroup>
                                </select>
                                <button
                                  className="order-claim-btn"
                                  onClick={() => handleClaimForOwner(o)}
                                  disabled={!claimSel[o.id] || claimingId === o.id}
                                >
                                  {claimingId === o.id ? 'Claiming…' : 'Claim order'}
                                </button>
                              </div>
                              <div className="order-claim-hint">
                                Assigns this order's {o.orderCount} products to the chosen owner (same as the customer claiming it). No email/second-factor needed.
                              </div>
                            </div>
                          )}
                          <div className="order-expand-danger">
                            <button
                              className="order-action-btn"
                              onClick={() => handleTransferSerial(o)}
                              title="Move a serial off this order onto the correct order"
                            >
                              Transfer a serial…
                            </button>
                            <button
                              className="order-action-btn"
                              onClick={() => handleArchiveOrder(o)}
                              title={o.archived ? 'Unarchive this order' : 'Archive this order (e.g. a school that doesn’t use the app)'}
                            >
                              {o.archived ? 'Unarchive order' : 'Archive order'}
                            </button>
                            <button
                              className="order-delete-btn"
                              onClick={() => handleDeleteOrder(o)}
                              disabled={deletingId === o.id}
                              title="Permanently delete this order (its bags are kept, just unlinked)"
                            >
                              {deletingId === o.id ? 'Deleting…' : 'Delete order'}
                            </button>
                          </div>
                          {(serials[o.id] || []).length === 0 ? (
                            <div className="order-expand-empty">No products linked to this order.</div>
                          ) : (
                          <div className="order-expand-content">
                            <div className="order-expand-section">
                              <div className="order-expand-title">Products ({o.orderCount})</div>
                              <table className="order-expand-table">
                                <thead>
                                  <tr><th>Product</th><th>SKU</th><th className="num">Qty</th></tr>
                                </thead>
                                <tbody>
                                  {productSummary(serials[o.id]).map(p => (
                                    <tr key={p.sku}>
                                      <td>{p.displayName}</td>
                                      <td className="mono">{p.sku}</td>
                                      <td className="num">{p.count}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <div className="order-expand-section">
                              <div className="order-expand-title">Serial numbers</div>
                              <table className="order-expand-table">
                                <thead>
                                  <tr><th>Serial number</th><th>SKU</th><th>Product</th></tr>
                                </thead>
                                <tbody>
                                  {(serials[o.id] || []).map(s => (
                                    <tr key={s.serialNumber}>
                                      <td className="mono">{s.serialNumber}</td>
                                      <td className="mono">{s.sku}</td>
                                      <td>{s.displayName}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          )}
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {legacyPreview && (
        <div className="modal-overlay" onClick={() => !creatingLegacy && setLegacyPreview(null)}>
          <div className="legacy-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Create order from spreadsheet</h3>
            <div className="legacy-modal-summary">
              <div>
                <span className="legacy-lbl">Order / invoice #</span>
                <input
                  className="legacy-val legacy-orderno-input"
                  value={legacyOrderNo}
                  onChange={(e) => setLegacyOrderNo(e.target.value)}
                  placeholder="Enter order # (not found in sheet)"
                  disabled={creatingLegacy}
                />
              </div>
              {legacyPreview.company ? <div><span className="legacy-lbl">Customer</span><span className="legacy-val">{legacyPreview.company}</span></div> : null}
              <div><span className="legacy-lbl">Bags</span><span className="legacy-val">{legacyPreview.rows.length}</span></div>
            </div>
            <table className="legacy-modal-table">
              <thead><tr><th>Model</th><th className="num">Qty</th></tr></thead>
              <tbody>
                {legacyBreakdown.map(b => (
                  <tr key={b.model}><td>{b.model}</td><td className="num">{b.count}</td></tr>
                ))}
              </tbody>
            </table>
            <p className="legacy-modal-note">
              This creates an <strong>unclaimed</strong> order #{legacyOrderNo || '-'} with these {legacyPreview.rows.length} bags. The customer can then claim it with that order number.
            </p>
            <div className="legacy-modal-actions">
              <button className="legacy-cancel" onClick={() => setLegacyPreview(null)} disabled={creatingLegacy}>Cancel</button>
              <button className="legacy-create" onClick={handleCreateLegacy} disabled={creatingLegacy || !legacyOrderNo.trim()}>
                {creatingLegacy ? 'Creating…' : `Create order (${legacyPreview.rows.length} bags)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Persistent upload result — stays until "Done"; does NOT close on an outside click, so the
          skipped-serial list can be read and copied. */}
      {legacyResult && (
        <div className="modal-overlay">
          <div className="legacy-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Order {legacyResult.orderNumber} created</h3>
            <p className="legacy-modal-note">
              <strong>{legacyResult.recorded}</strong> bag(s) added — ready to claim.
            </p>
            {legacyResult.conflicts.length > 0 ? (
              <>
                <p className="legacy-modal-note">
                  <strong>{legacyResult.conflicts.length} serial(s) could NOT be added</strong> — already on another order:
                </p>
                <table className="legacy-modal-table">
                  <thead><tr><th>Serial</th><th>Already on order</th></tr></thead>
                  <tbody>
                    {legacyResult.conflicts.map(c => (
                      <tr key={c.serialNumber}><td>{c.serialNumber}</td><td>{c.orderNumber}</td></tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : (
              <p className="legacy-modal-note">All serials were added — no conflicts.</p>
            )}
            {legacyResult.badFormat && legacyResult.badFormat.length > 0 ? (
              <>
                <p className="legacy-modal-note">
                  <strong>{legacyResult.badFormat.length} serial(s)</strong> don't match the standard number format — filed as tag reports to review:
                </p>
                <div className="legacy-badformat-list">{legacyResult.badFormat.join(', ')}</div>
              </>
            ) : null}
            <div className="legacy-modal-actions">
              <button className="legacy-create" onClick={() => setLegacyResult(null)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AllOrdersTable;
